import * as R from 'ramda'
import { of } from 'rxjs'
import {
  catchError,
  delayWhen,
  distinctUntilChanged,
  filter,
  startWith,
  switchMap
} from 'rxjs/operators'
import { LOCATION_CHANGE } from 'connected-react-router'
import { ofType } from 'redux-observable'
import { fetchLastViewedProducts } from 'api/planet/lastViewedProducts'
import { isArray } from 'helpers/typeof'
import * as Actions from 'store/actions/index.ts'
import { awaitForSaleFetched } from 'store/epics/helpers'
import {
  getIsLastViewedProductFeatureEnabled,
  getIsSaleFetched,
  getLocaleFromApplication,
  getMarketId
} from 'store/selectors'
import { isCatalogOrUniversePageAction, isProductPageAction } from 'domain/router'
import { captureException } from 'helpers/raven'

// type IsLastViewedProductsValid = any => boolean
const isLastViewedProductsFetchResponseValid = products => (
  isArray(products) &&
  Boolean(products.length) &&
  products.every(product => (
    product && product.key
  ))
)

// type OnInvalidLastViewedProductsFetchResponse = (Error, boolean) => ActionsObservable
const onInvalidLastViewedProductsFetchResponse = (error, isSentryEnabled) => {
  if (isSentryEnabled) {
    captureException(new Error('Unable to fetch last viewed products'), {
      extra: { error },
      level: 'warning'
    })
  }

  return of(Actions.lastViewedProducts.onLastViewedProductsFetchError(error))
}

const lastViewedProductsEpic = (actions$, state$) => actions$.pipe(
  ofType(LOCATION_CHANGE),
  delayWhen(() => {
    const resourceFetched$ = actions$.pipe(ofType(Actions.resources.RESOURCE_FETCHED))

    return (getIsSaleFetched(state$.value))
      ? of({})
      : awaitForSaleFetched(resourceFetched$, state$)
  }),
  filter(action => (
    getIsLastViewedProductFeatureEnabled(state$.value) &&
      (isProductPageAction(action) || isCatalogOrUniversePageAction(action))
  )),
  distinctUntilChanged((previousAction, currentAction) => {
    if (isProductPageAction(currentAction)) {
      return false
    }

    if (isCatalogOrUniversePageAction(currentAction) && !isCatalogOrUniversePageAction(previousAction)) {
      return false
    }

    return true
  }),
  distinctUntilChanged(R.eqBy(R.path(['payload', 'location', 'pathname']))),
  switchMap(() => {
    const state = state$.value
    const marketId = getMarketId(state)
    const locale = getLocaleFromApplication(state)

    return fetchLastViewedProducts(marketId, locale).pipe(
      switchMap(({ response }) => (
        isLastViewedProductsFetchResponseValid(response)
          ? of(Actions.lastViewedProducts.onLastViewProductsFetched(response))
          : onInvalidLastViewedProductsFetchResponse(new Error('Corrupted last viewed products'), false)
      )),
      startWith(Actions.lastViewedProducts.onFetchLastViewedProductsStarted())
    )
  }),
  catchError(error => onInvalidLastViewedProductsFetchResponse(error, true))
)

export default lastViewedProductsEpic
